import React, {
  createContext,
  useRef,
  RefObject,
  useCallback,
  PropsWithChildren,
  useState,
} from 'react';

interface LayoutElementsContextType {
  appBarRef: RefObject<HTMLElement | null>;
  pageHeaderRef: RefObject<HTMLElement | null>;
  homeLeftSectionRef: RefObject<HTMLElement | null>;
  homeRightSectionRef: RefObject<HTMLElement | null>;
  taskModalRef: RefObject<HTMLElement | null>;

  setAppBarRef: (ref: HTMLElement | null) => void;
  setPageHeaderRef: (ref: HTMLElement | null) => void;
  setHomeLeftSectionRef: (ref: HTMLElement | null) => void;
  setHomeRightSectionRef: (ref: HTMLElement | null) => void;
  setTaskModalRef: (ref: HTMLElement | null) => void;

  isLeftModalOpen: boolean;
  setIsLeftModalOpen: (active: boolean) => void;

  isRightModalOpen: boolean;
  setIsRightModalOpen: (active: boolean) => void;

  isAlertOpen: boolean;
  setIsAlertOpen: (active: boolean) => void;
}

export const LayoutElementsContext = createContext<LayoutElementsContextType>({
  appBarRef: { current: null },
  pageHeaderRef: { current: null },
  homeLeftSectionRef: { current: null },
  homeRightSectionRef: { current: null },
  taskModalRef: { current: null },

  setAppBarRef: () => {},
  setPageHeaderRef: () => {},
  setHomeLeftSectionRef: () => {},
  setHomeRightSectionRef: () => {},
  setTaskModalRef: () => {},

  isLeftModalOpen: false,
  setIsLeftModalOpen: () => {},

  isRightModalOpen: false,
  setIsRightModalOpen: () => {},

  isAlertOpen: false,
  setIsAlertOpen: () => {},
});

export const LayoutElementsContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const appBarRef = useRef<HTMLElement | null>(null);
  const pageHeaderRef = useRef<HTMLElement | null>(null);
  const homeLeftSectionRef = useRef<HTMLElement | null>(null);
  const homeRightSectionRef = useRef<HTMLElement | null>(null);
  const taskModalRef = useRef<HTMLElement | null>(null);

  const [isLeftModalOpen, setIsLeftModalOpen] = useState(false);
  const [isRightModalOpen, setIsRightModalOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const setAppBarRef = useCallback((ref: HTMLElement | null) => {
    if (ref) {
      appBarRef.current = ref;
    }
    return () => {
      appBarRef.current = null;
    };
  }, []);

  const setPageHeaderRef = useCallback((ref: HTMLElement | null) => {
    if (ref) {
      pageHeaderRef.current = ref;
    }
    return () => {
      pageHeaderRef.current = null;
    };
  }, []);

  const setHomeLeftSectionRef = useCallback((ref: HTMLElement | null) => {
    if (ref) {
      homeLeftSectionRef.current = ref;
    }
    return () => {
      homeLeftSectionRef.current = null;
    };
  }, []);

  const setHomeRightSectionRef = useCallback((ref: HTMLElement | null) => {
    if (ref) {
      homeRightSectionRef.current = ref;
    }
    return () => {
      homeRightSectionRef.current = null;
    };
  }, []);

  const setTaskModalRef = useCallback((ref: HTMLElement | null) => {
    if (ref) {
      taskModalRef.current = ref;
    }
    return () => {
      taskModalRef.current = null;
    };
  }, []);

  return (
    <LayoutElementsContext.Provider
      value={{
        appBarRef,
        pageHeaderRef,
        homeLeftSectionRef,
        homeRightSectionRef,
        taskModalRef,

        setAppBarRef,
        setPageHeaderRef,
        setHomeLeftSectionRef,
        setHomeRightSectionRef,
        setTaskModalRef,

        isLeftModalOpen,
        isRightModalOpen,
        isAlertOpen,

        setIsLeftModalOpen,
        setIsRightModalOpen,
        setIsAlertOpen,
      }}
    >
      {children}
    </LayoutElementsContext.Provider>
  );
};
